import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import { useTheme } from '@mui/material';
import { useMatch } from 'react-router';
import { getRouteTitle, ROUTES } from '../../../../../constants/RouteNameMapping';
import { useBeneficialOwnershipEnabled } from '../../../../../pages/EntityManagement/hooks/useBeneficialOwnershipEnabled';
import { useEntityManagementCanEdit } from '../../../../../pages/EntityManagement/hooks/useEntityManagementCanEdit';
import { useEntityManagementEnabled } from '../../../../../pages/EntityManagement/hooks/useEntityManagementEnabled';
import { usePendingProposalCount } from '../../../../../pages/EntityManagement/surfaces/ReviewQueues/useProposalQueue';
import SubSectionNavItems, { NavItemType } from '../../SubSectionNavigation/SubSectionNavItems';
import { SubSectionNavigation } from '../../SubSectionNavigation/SubSectionNavigation';
import { NavigationWrapper } from '../NavigationWrapper';

const ICON_SIZE = '1rem';

interface IEntityManagementNavigationProps {
  /**
   * Unfiltered entity count for the registry badge. Owned by this shell rather than
   * the registry surface so the badge is present before FD-4472 lands; it stays
   * undefined (badge hidden) until the registry read is wired.
   */
  entityCount?: number;
}

export function EntityManagementNavigation({ entityCount }: IEntityManagementNavigationProps) {
  const match = useMatch(`/${ROUTES.ENTITIES}/*`);
  const isEnabled = useEntityManagementEnabled();
  const iconColor = useTheme().colors.secondary['10'];
  // Deciding is INTERNAL-only and is all the queue is for, so a client would get a badge
  // counting work they cannot do. Hidden, per useEntityManagementCanEdit.
  const canDecideProposals = useEntityManagementCanEdit();
  const showBeneficialOwnership = useBeneficialOwnershipEnabled();
  // Read here, not in the surface: the badge has to be right on every screen in the
  // module. Gated, so nobody who could not act on the count fetches it.
  const pendingProposalCount = usePendingProposalCount(canDecideProposals);

  if (match === null || !isEnabled) {
    return null;
  }

  // Keep every glyph on the same MUI outline weight. `PersonOutlineOutlined` is the
  // thinnest outline MUI ships and `GroupsOutlined` packs three figures into the full 24
  // grid, so beside each other one read faint and the other a blob.
  const iconProps = { htmlColor: iconColor, sx: { fontSize: ICON_SIZE } };

  const structureSections: NavItemType[] = [
    {
      id: ROUTES.ENTITIES_STRUCTURE_CHART,
      value: getRouteTitle(ROUTES.ENTITIES_STRUCTURE_CHART),
      IconSlot: <AccountTreeOutlinedIcon {...iconProps} />,
    },
    {
      id: ROUTES.ENTITIES_REGISTRY,
      value: getRouteTitle(ROUTES.ENTITIES_REGISTRY),
      IconSlot: <ListAltOutlinedIcon {...iconProps} />,
      badgeIndicator: entityCount,
    },
  ];

  const complianceSections: NavItemType[] = [
    {
      id: ROUTES.ENTITIES_DO_SLATE,
      value: getRouteTitle(ROUTES.ENTITIES_DO_SLATE),
      IconSlot: <PeopleOutlinedIcon {...iconProps} />,
    },
    // Put away for the first release; see useBeneficialOwnershipEnabled.
    ...(showBeneficialOwnership
      ? [
          {
            id: ROUTES.ENTITIES_BENEFICIAL_OWNERSHIP,
            value: getRouteTitle(ROUTES.ENTITIES_BENEFICIAL_OWNERSHIP),
            IconSlot: <PersonOutlinedIcon {...iconProps} />,
          },
        ]
      : []),
    ...(canDecideProposals
      ? [
          {
            id: ROUTES.ENTITIES_REVIEW_QUEUES,
            value: getRouteTitle(ROUTES.ENTITIES_REVIEW_QUEUES),
            // Approve/reject marks, not a framed document: the registry above already
            // spends the nav's one boxed glyph.
            IconSlot: <RuleOutlinedIcon {...iconProps} />,
            // Falsy hides the badge in SubSectionNavItems, so a cleared queue shows no
            // count rather than a zero.
            badgeIndicator: pendingProposalCount,
          },
        ]
      : []),
  ];

  return (
    <NavigationWrapper title={getRouteTitle(ROUTES.ENTITIES)}>
      <SubSectionNavItems sectionList={structureSections} parentRoute={ROUTES.ENTITIES} />
      <SubSectionNavigation title='Compliance'>
        <SubSectionNavItems sectionList={complianceSections} parentRoute={ROUTES.ENTITIES} />
      </SubSectionNavigation>
    </NavigationWrapper>
  );
}
